import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import ProtectedRoute from './ProtectedRoute';
import userStore from '../stores/UserStore';
import { ProjectManager } from '../models';
import { useAdminNav } from '../hooks/useAdminNav';
import { useAppTheme } from '../hooks/useAppTheme';
import type { ThemedSxStyle } from '../types/themedStyles';
import { $loadingContainer } from '../theme/styles';
import { canAccessPlatformAppTools } from '../utilities/platformAdminAccess';

interface AdminOnlyRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
}

const AdminOnlyRoute: React.FC<AdminOnlyRouteProps> = observer(({ 
  children, 
  redirectTo = '/loginscreen'
}) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const location = useLocation();
  const { themed, theme: appTheme } = useAppTheme();
  const { selectedProject } = useAdminNav();

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        // Check if user is demo user - demo users always have access
        const isDemoUser = userStore.user?.userId === 'demo-user';
        
        if (isDemoUser) {
          setHasAccess(true);
          setIsCheckingAuth(false);
          return;
        }

        // Platform admins (superAdmin + limited admin) always have access
        if (canAccessPlatformAppTools(userStore.user?.userRole)) {
          setHasAccess(true);
          setIsCheckingAuth(false);
          return;
        }

        // If not platform admin, check if user is project owner
        // Non-platform admins need a project to check ownership
        if (!selectedProject) {
          setHasAccess(false);
          setIsCheckingAuth(false);
          return;
        }

        // Fetch project to check ownership
        const project = await ProjectManager.getProject(selectedProject);
        if (!project) {
          setHasAccess(false);
          setIsCheckingAuth(false);
          return;
        }

        // Check if current user is the project owner
        const isProjectOwner = userStore.user?.userId === project.projectOwnerUserKey;
        setHasAccess(isProjectOwner);
      } catch (error) {
        console.error('Error checking admin access:', error);
        setHasAccess(false);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    // Only check if user is authenticated
    if (userStore.user) {
      checkAdminAccess();
    } else {
      setIsCheckingAuth(false);
      setHasAccess(false);
    }
  }, [selectedProject, userStore.user?.userId, userStore.user?.userRole]);

  // Show loading while checking
  if (isCheckingAuth) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        sx={themed([$loadingContainer, $loadingContainerBg])}
      >
        <CircularProgress sx={themed($progress)} />
        <Typography variant="body1" sx={themed($loadingText)}>
          Checking access permissions...
        </Typography>
      </Box>
    );
  }

  // If no access, show error message
  if (hasAccess === false) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="50vh"
        sx={themed($errorContainer)}
      >
        <Alert 
          severity="error" 
          sx={themed($errorAlert)}
        >
          <Typography variant="h6" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1">
            You do not have permission to access this area. Only super admins and project administrators can access this page.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // If has access, render the protected route (which will handle authentication)
  return (
    <ProtectedRoute redirectTo={redirectTo}>
      {children}
    </ProtectedRoute>
  );
});

const $loadingContainerBg: ThemedSxStyle = ({ colors }) => ({
  backgroundColor: colors.background,
  transition: 'background-color 0.3s ease'
});

const $progress: ThemedSxStyle = ({ colors }) => ({
  color: colors.palette.main,
  mb: 2
});

const $loadingText: ThemedSxStyle = ({ colors }) => ({
  color: colors.text
});

const $errorContainer: ThemedSxStyle = ({ colors }) => ({
  backgroundColor: colors.background,
  transition: 'background-color 0.3s ease',
  p: 3
});

const $errorAlert: ThemedSxStyle = () => ({
  maxWidth: 600,
  '& .MuiAlert-message': {
    width: '100%'
  }
});

export default AdminOnlyRoute;

